/**
 * Inventory Service Extensions
 *
 * Purpose: Extend existing inventoryService with new offline-first functionality
 * Maintains backward compatibility while adding new features
 *
 * New features:
 *   - IndexedDB-based queue
 *   - Centralized RPC naming
 *   - Event bus integration
 *   - Cache management
 *
 * Usage:
 *   import { extendedInventoryService } from '@/services/inventory/inventoryServiceExtensions';
 *   // Use extendedInventoryService instead of inventoryService
 *
 * Reference: LeltárMester Egyesített Refaktorálási Terv (v2.0)
 */

import { inventoryService as originalInventoryService } from '../inventoryService';
import { syncService } from '../syncService';
import { db, type CachedInventoryItem } from '@/lib/db';
import { eventBus, EVENT_TYPES } from '@/utils/eventBus';
import { getRpcName } from '@/utils/rpc';
import { logger } from '../logger';
import { addInventoryOperationToQueue, migrateLegacyQueue } from './inventoryQueueBridge';
import type { SupplierProduct, ScanMode } from '@/components/types';

// ============================================================================
// Extended Inventory Service
// ============================================================================

/**
 * Extended version of inventoryService with new features
 */
export const extendedInventoryService = {
  // Re-export all original methods for backward compatibility
  ...originalInventoryService,

  // ========================================================================
  // NEW: Queue Management (IndexedDB)
  // ========================================================================

  /**
   * Get current queue size (new IndexedDB queue)
   */
  async getQueueSizeNew(): Promise<number> {
    return await syncService.getQueueSize();
  },

  /**
   * Process offline queue (delegates to syncService)
   */
  async processQueueNew(): Promise<void> {
    const result = await syncService.processQueue();
    logger.info('Queue processed', result, 'InventoryService');
  },

  /**
   * Add operation to queue (new way)
   */
  async addToQueueNew(
    ean: string,
    delta: number,
    companyId: string,
    reason: ScanMode = 'COUNT',
    productDetails?: SupplierProduct
  ): Promise<void> {
    await addInventoryOperationToQueue(ean, delta, companyId, reason, productDetails);

    // Emit event for UI updates
    eventBus.emit(EVENT_TYPES.INVENTORY_UPDATED, { ean, delta, reason });
  },

  /**
   * Migrate legacy queue to new IndexedDB queue
   */
  async migrateLegacyQueue(): Promise<number> {
    return await migrateLegacyQueue();
  },

  // ========================================================================
  // NEW: Cache Management (IndexedDB)
  // ========================================================================

  /**
   * Get cached inventory item (from IndexedDB)
   */
  async getCachedItem(ean: string): Promise<CachedInventoryItem | undefined> {
    try {
      return await db.inventoryCache.get(ean);
    } catch (error) {
      logger.error('Failed to get cached item', error, 'InventoryService');
      return undefined;
    }
  },

  /**
   * Cache inventory item (to IndexedDB)
   */
  async cacheItem(item: CachedInventoryItem): Promise<void> {
    try {
      await db.inventoryCache.put({
        ...item,
        cachedAt: Date.now(),
      });
      logger.debug(`Cached item: ${item.ean}`, undefined, 'InventoryService');
    } catch (error) {
      logger.error('Failed to cache item', error, 'InventoryService');
    }
  },

  /**
   * Cache multiple inventory items (bulk operation)
   */
  async cacheItems(items: CachedInventoryItem[]): Promise<void> {
    try {
      const now = Date.now();
      const itemsWithTimestamp = items.map((item) => ({
        ...item,
        cachedAt: now,
      }));

      await db.inventoryCache.bulkPut(itemsWithTimestamp);
      logger.info(`Cached ${items.length} items`, undefined, 'InventoryService');
    } catch (error) {
      logger.error('Failed to cache items', error, 'InventoryService');
    }
  },

  /**
   * Clear inventory cache
   */
  async clearCache(): Promise<void> {
    try {
      await db.inventoryCache.clear();
      logger.info('Inventory cache cleared', undefined, 'InventoryService');
      eventBus.emit(EVENT_TYPES.CACHE_CLEARED, { type: 'inventory' });
    } catch (error) {
      logger.error('Failed to clear cache', error, 'InventoryService');
    }
  },

  /**
   * Get cached items by company (for offline access)
   */
  async getCachedItemsByCompany(companyId: string): Promise<CachedInventoryItem[]> {
    try {
      return await db.inventoryCache.where('companyId').equals(companyId).toArray();
    } catch (error) {
      logger.error('Failed to get cached items by company', error, 'InventoryService');
      return [];
    }
  },

  // ========================================================================
  // NEW: Safe RPC Calls (using centralized utility)
  // ========================================================================

  /**
   * Get RPC name with schema suffix
   * (Wraps utility for convenience)
   */
  getRpcName(baseName: string): string {
    return getRpcName(baseName);
  },

  // ========================================================================
  // NEW: Event-Driven Updates
  // ========================================================================

  /**
   * Emit inventory updated event
   */
  emitInventoryUpdated(ean: string, data?: Record<string, unknown>): void {
    eventBus.emit(EVENT_TYPES.INVENTORY_UPDATED, { ean, ...(data || {}) });
  },

  /**
   * Subscribe to inventory updates
   */
  onInventoryUpdated(callback: (payload: unknown) => void): () => void {
    return eventBus.on(EVENT_TYPES.INVENTORY_UPDATED, callback);
  },

  // ========================================================================
  // NEW: Offline-First Operations
  // ========================================================================

  /**
   * Update item quantity (offline-first)
   *
   * This is the production-ready way to update inventory:
   * 1. Add to queue (IndexedDB)
   * 2. If online, sync immediately
   * 3. If offline, sync when back online
   *
   * Status: ACTIVE - This is the recommended method for all inventory updates
   */
  async updateItemQuantityOfflineFirst(
    ean: string,
    delta: number,
    companyId: string,
    reason: ScanMode = 'COUNT',
    productDetails?: SupplierProduct
  ): Promise<void> {
    // Add to queue
    await this.addToQueueNew(ean, delta, companyId, reason, productDetails);

    // If online, process immediately
    if (navigator.onLine) {
      logger.info('Online: processing queue immediately', undefined, 'InventoryService');
      await this.processQueueNew();
    } else {
      logger.info('Offline: queued for later sync', undefined, 'InventoryService');
    }
  },

  // ========================================================================
  // Utilities
  // ========================================================================

  /**
   * Check if offline mode is active
   */
  isOffline(): boolean {
    return !navigator.onLine;
  },

  /**
   * Get sync statistics
   */
  async getSyncStats() {
    const queueStats = await syncService.getQueueStats();
    const cacheSize = await db.inventoryCache.count();

    return {
      queue: queueStats,
      cache: {
        inventoryItems: cacheSize,
      },
      isOffline: this.isOffline(),
    };
  },
};

// ============================================================================
// Note: Legacy queue migration is handled by useInventorySync hook on app mount
// ============================================================================
